/**
 * Store de gestion du layout (sidebar, responsive)
 */
import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useLocalStorage, useWindowSize } from '@vueuse/core'

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 72
const MOBILE_BREAKPOINT = 1024

export const useLayoutStore = defineStore('layout', () => {
  // ============ STATE ============
  
  // État de la sidebar (persisté)
  const isSidebarCollapsed = useLocalStorage('pdm-sidebar-collapsed', false)
  
  // État mobile
  const isMobileSidebarOpen = ref(false)
  
  // Groupes de navigation expandés (persisté)
  const expandedGroups = useLocalStorage('pdm-expanded-groups', [])
  
  // ============ GETTERS ============
  
  const { width: windowWidth } = useWindowSize()
  
  const isMobile = computed(() => windowWidth.value < MOBILE_BREAKPOINT)
  
  const sidebarWidth = computed(() => 
    isSidebarCollapsed.value ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
  )
  
  const isGroupExpanded = (groupName) => {
    return expandedGroups.value.includes(groupName)
  }
  
  // ============ ACTIONS ============
  
  const toggleSidebar = () => {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
  }
  
  const toggleMobileSidebar = () => {
    isMobileSidebarOpen.value = !isMobileSidebarOpen.value
  }
  
  const closeMobileSidebar = () => {
    isMobileSidebarOpen.value = false
  }
  
  const toggleGroup = (groupName) => {
    const index = expandedGroups.value.indexOf(groupName)
    if (index === -1) {
      expandedGroups.value.push(groupName)
    } else {
      expandedGroups.value.splice(index, 1)
    }
  }
  
  const expandGroup = (groupName) => {
    if (!expandedGroups.value.includes(groupName)) {
      expandedGroups.value.push(groupName)
    }
  }
  
  const collapseGroup = (groupName) => {
    const index = expandedGroups.value.indexOf(groupName)
    if (index !== -1) {
      expandedGroups.value.splice(index, 1)
    }
  }
  
  // Fermer le menu mobile quand on passe en desktop
  watch(isMobile, (newVal) => {
    if (!newVal) {
      isMobileSidebarOpen.value = false
    }
  })
  
  return {
    // State
    isSidebarCollapsed,
    isMobileSidebarOpen,
    expandedGroups,
    
    // Getters
    isMobile,
    sidebarWidth,
    isGroupExpanded,
    
    // Actions
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    toggleGroup,
    expandGroup,
    collapseGroup,
  }
})
